// useShareLinks.js - Hook for share link functionality with NEW URL format support

import { useState } from 'react';
import { createShareLink } from '../utils/shareUtils';

/**
 * Hook for managing share links with NEW URL format support
 * 
 * NEW: Now returns both date-specific and "today" links in new format
 * LEGACY: Still supports old random-ID system via createShareLink
 * 
 * Usage:
 * const { 
 *   shareLink,           // Legacy random ID link (backward compatibility)
 *   newDateLink,         // NEW: /share/{clubId}/{date}
 *   newTodayLink,        // NEW: /share/{clubId}/today
 *   showShareDialog, 
 *   showPaywall,
 *   handleShare, 
 *   closeShareDialog,
 *   closePaywall,
 *   shareMetadata
 * } = useShareLinks(clubInfo, canCreateShareLinks);
 * 
 * @param {object} clubInfo - Club information including clubId and name
 * @param {boolean} canCreateShareLinks - Whether user's tier allows share links
 * @returns {object} Share link state and handlers
 */
export const useShareLinks = (clubInfo, canCreateShareLinks) => {
  const [shareLink, setShareLink] = useState(''); // Legacy link
  const [newDateLink, setNewDateLink] = useState(''); // NEW format: date-specific
  const [newTodayLink, setNewTodayLink] = useState(''); // NEW format: today
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [shareMetadata, setShareMetadata] = useState(null);

  /**
   * Handle share button click
   * - Shows paywall if subscription tier doesn't allow share links
   * - Creates BOTH new format links AND legacy link
   * 
   * @param {Object} shareParams - Parameters for the share link
   * @param {string} shareParams.pitchId - Pitch identifier
   * @param {string} shareParams.date - Date in YYYY-MM-DD format
   * @param {string} [shareParams.facilityId] - Facility identifier (optional)
   * @param {string} [shareParams.facilityName] - Facility name for display (optional)
   * @param {number} [shareParams.start] - Start hour (default: 6)
   * @param {number} [shareParams.end] - End hour (default: 22)
   * @param {number} [shareParams.expiryDays] - Days until expiration (default: 30)
   */
  const handleShare = async (shareParams) => {
    // Check subscription tier first
    if (!canCreateShareLinks) {
      setShowPaywall(true);
      return;
    }

    try {
      // Generate NEW format links (no Firebase needed!)
      const baseUrl = window.location.origin;
      const clubId = clubInfo.clubId;
      const date = shareParams.date;
      
      const newDateLinkUrl = `${baseUrl}/share/${clubId}/${date}`;
      const newTodayLinkUrl = `${baseUrl}/share/${clubId}/today`;
      
      setNewDateLink(newDateLinkUrl);
      setNewTodayLink(newTodayLinkUrl);

      // OPTIONAL: Also create legacy link for backward compatibility
      // Comment this out if you want to stop creating legacy links
      const legacyResult = await createShareLink({
        clubId: clubInfo.clubId,
        clubName: clubInfo.name,
        ...shareParams
      });

      if (legacyResult.success) {
        setShareLink(legacyResult.link);
        setShareMetadata({
          ...legacyResult.metadata,
          // Add new format links to metadata
          newDateLink: newDateLinkUrl,
          newTodayLink: newTodayLinkUrl
        });
      } else {
        // Even if legacy creation fails, we can still use new format
        console.warn('Legacy link creation failed, using new format only:', legacyResult.error);
        setShareLink(''); // No legacy link
        setShareMetadata({
          date: shareParams.date,
          facilityName: shareParams.facilityName,
          newDateLink: newDateLinkUrl,
          newTodayLink: newTodayLinkUrl
        });
      }

      setShowShareDialog(true);
      console.log('✅ New format links created:');
      console.log('   Date-specific:', newDateLinkUrl);
      console.log('   Today link:', newTodayLinkUrl);
      if (legacyResult?.success) {
        console.log('   Legacy link:', legacyResult.link);
      }
      if (shareParams.facilityId) {
        console.log('🏟️ For facility:', shareParams.facilityName || shareParams.facilityId);
      }

    } catch (error) {
      console.error('Error in handleShare:', error);
      
      // Even if there's an error, try to show new format links
      const baseUrl = window.location.origin;
      const clubId = clubInfo.clubId;
      const date = shareParams.date;
      
      setNewDateLink(`${baseUrl}/share/${clubId}/${date}`);
      setNewTodayLink(`${baseUrl}/share/${clubId}/today`);
      setShareMetadata({
        date: shareParams.date,
        facilityName: shareParams.facilityName
      });
      setShowShareDialog(true);
    }
  };

  /**
   * Close the share dialog
   */
  const closeShareDialog = () => {
    setShowShareDialog(false);
    setShareLink('');
    setNewDateLink('');
    setNewTodayLink('');
    setShareMetadata(null);
  };

  /**
   * Close the paywall modal
   */
  const closePaywall = () => {
    setShowPaywall(false);
  };

  return {
    shareLink,        // Legacy random ID link (for backward compatibility)
    newDateLink,      // NEW: /share/{clubId}/{date}
    newTodayLink,     // NEW: /share/{clubId}/today
    shareMetadata,
    showShareDialog,
    showPaywall,
    handleShare,
    closeShareDialog,
    closePaywall
  };
};

export default useShareLinks;
